import { HStack, PageContainer, Text, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { DatePicker } from '@/components/DatePicker'
import { DetailsHeader } from '@/components/DetailsHeader'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import { Spinner } from '@/components/Spinner'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  TaxAssumption,
  TaxAssumptionDetailFromApi,
  taxAssumptionByIdQueryOptions,
  useTaxAssumptionById,
  useUpdateTaxAssumption,
  useUpdateTaxAssumptionDetails,
} from '@/hooks/queries/crt/useTaxAssumptions'
import { useLastUpdatedTaxAssumptionLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateTaxAssumption } from '@repo/backend/crt'
import { PRODUCT_CATEGORIES } from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PinnedByVersionsNote } from '../../-components/PinnedByVersionsNote'
import { z } from 'zod'
import { MissingPriceListNote } from '../-components/MissingPriceListNote'
import { TaxAssumptionEntries } from './-components/TaxAssumptionDetails/TaxAssumptionEntries'

const taxAssumptionDetailsSearchSchema = z.object({
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  materialGroupCode: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  province: z.array(z.string()).optional(),
  unmarkedProvince: z.array(z.string()).optional(),
  channel: z.array(z.string()).optional(),
  keyword: z.string().optional(),
  sort: z
    .object({
      id: z.string(),
      desc: z.boolean(),
    })
    .optional(),
})

export const Route = createFileRoute(
  '/_auth/_modules/crt/pricing-excise/tax-assumptions/$taxAssumptionId',
)({
  component: TaxAssumptionDetails,
  validateSearch: (search) => taxAssumptionDetailsSearchSchema.parse(search),
  loader: async ({ params: { taxAssumptionId } }) => {
    const taxAssumption = await queryClient.ensureQueryData(
      taxAssumptionByIdQueryOptions(taxAssumptionId),
    )

    return {
      crumb: taxAssumption.name,
    }
  },
})

function TaxAssumptionDetails() {
  const { t } = useTranslation('crt')
  const { canUser } = usePermissions()
  const { taxAssumptionId } = Route.useParams()
  const search = Route.useSearch()

  const { data, isPending, isError } = useTaxAssumptionById(taxAssumptionId, search)
  const { data: lastUpdatedTaxAssumptionTrail } = useLastUpdatedTaxAssumptionLog(taxAssumptionId)

  const [formState, setFormState] = useState({
    name: '',
    description: '',
    effectiveDate: new Date(),
  })

  const [taxAssumption, setTaxAssumption] = useState<TaxAssumption | null>(null)

  useEffect(() => {
    if (data) {
      setFormState({
        name: data.name,
        description: data.description ?? '',
        effectiveDate: data.effectiveDate,
      })
      setTaxAssumption({
        ...data,
        effectiveDate: data.effectiveDate,
      })
    }
  }, [data])

  const hasUnsavedPriceAdjustments = taxAssumption
    ? taxAssumption.details.some((detail) => 'isUnsaved' in detail && detail.isUnsaved)
    : false

  const getUnsavedAssumptionChanges = (): UpdateTaxAssumption | null => {
    if (!data) return null

    const changes: UpdateTaxAssumption = {}
    let hasChanges = false

    if (formState.name !== data.name) {
      changes.name = formState.name
      hasChanges = true
    }

    if (formState.description !== (data.description ?? '')) {
      changes.description = formState.description
      hasChanges = true
    }

    if (formState.effectiveDate.getTime() !== data.effectiveDate.getTime()) {
      changes.effectiveDate = formatDateOnly(formState.effectiveDate)
      hasChanges = true
    }

    return hasChanges ? changes : null
  }

  const hasUnsavedChanges = hasUnsavedPriceAdjustments || getUnsavedAssumptionChanges() !== null

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedTaxAssumptionTrail)

  const { mutate: updateAssumption, isPending: isUpdateAssumptionPending } = useUpdateTaxAssumption(
    {
      taxAssumptionId: Number(taxAssumptionId),
    },
  )

  const { mutate: updateDetails, isPending: isUpdateDetailsPending } =
    useUpdateTaxAssumptionDetails(taxAssumptionId)

  const handleUpdateName = (name: string) => {
    setFormState((prev) => ({ ...prev, name }))
  }

  const handleUpdateDescription = (description: string) => {
    setFormState((prev) => ({ ...prev, description }))
  }

  const handleUpdateEffectiveDate = (date: Date | undefined) => {
    if (!date) return
    setFormState((prev) => ({ ...prev, effectiveDate: date }))
  }

  const handleBulkEditSave = (
    selectedDetails: TaxAssumptionDetailFromApi[],
    updates: Partial<TaxAssumptionDetailFromApi>,
  ) => {
    if (updates.priceAdjustment === undefined || !Number.isFinite(updates.priceAdjustment)) {
      return
    }

    const priceAdjustment = updates.priceAdjustment

    const apiUpdates = selectedDetails.map((detail) => ({
      id: detail.id,
      data: {
        priceAdjustment,
      },
    }))

    updateDetails(apiUpdates)
  }

  const handleSave = () => {
    if (!taxAssumption) return

    const unsavedAssumptionChanges = getUnsavedAssumptionChanges()

    const unsavedDetails = taxAssumption.details.filter(
      (detail) => 'isUnsaved' in detail && detail.isUnsaved,
    )

    if (!unsavedAssumptionChanges && unsavedDetails.length === 0) return

    if (unsavedAssumptionChanges) {
      updateAssumption(unsavedAssumptionChanges)
    }

    if (unsavedDetails.length > 0) {
      const apiUpdates = unsavedDetails.map((detail) => ({
        id: detail.id,
        data: {
          priceAdjustment: detail.priceAdjustment,
        },
      }))

      updateDetails(apiUpdates)
    }
  }

  if (isPending) {
    return (
      <PageContainer className="flex items-center justify-center">
        <Spinner size="lg" />
      </PageContainer>
    )
  }

  if (isError || !taxAssumption) {
    return (
      <PageContainer>
        <Text>{t('errors.taxAssumptions.fetchFailed')}</Text>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsavedChanges} />

      <MissingPriceListNote />

      <PinnedByVersionsNote driver="taxAssumptions" entityId={taxAssumptionId} />

      <HStack gap="sm" className="justify-between">
        <DetailsHeader
          name={formState.name}
          description={formState.description}
          onUpdateName={handleUpdateName}
          onUpdateDescription={handleUpdateDescription}
          permission="crt_pe_tax_assumptions_edit"
        />

        <VStack gap="md" className="items-end">
          <VStack gap="xs" className="items-end">
            <Text tone="muted" size="xs">
              {t('taxAssumptionDetails.effectiveDate')}
            </Text>
            <DatePicker
              defaultDate={formState.effectiveDate}
              onSelect={handleUpdateEffectiveDate}
              shouldWarnOnPastDate
              disabled={!canUser('crt_pe_tax_assumptions_edit')}
            />
          </VStack>
        </VStack>
      </HStack>

      <TaxAssumptionEntries
        taxAssumptionId={taxAssumptionId}
        taxAssumption={taxAssumption}
        setTaxAssumption={setTaxAssumption}
        onBulkEditSave={handleBulkEditSave}
      />

      <PermissionsGuard required={['crt_pe_tax_assumptions_edit']}>
        <PendingChanges
          areChangesPending={hasUnsavedChanges}
          isSavePending={isUpdateDetailsPending || isUpdateAssumptionPending}
          onSaveChanges={handleSave}
          lastUpdatedLog={lastUpdatedLog}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
